"use client";

import { DISABLE_TURNSTILE, SESSION_STORAGE_KEYS } from "../config";
import {
  ExtensionToIframeCommonHandlers,
  useExtensionListener,
} from "@/src/extension";
import React, { useEffect, useMemo, useState } from "react";
import { cn, setItem } from "utils";
import { deviceDetector, lazyHydrate } from "@ospweb/sdk-ui";
import {
  useAlphaModal,
  useFetchUserFolders,
  useDismissKeyboardOnOutsideTap,
  useInitApp,
  useContainerBroadcastStateSync,
  useCrossWebViewAuthSessionSync,
  useNativeTabFreezeSync,
  useNativeBridgeReconcile,
  useAdjustNativeIntegration,
  useIsPc,
  usePricingActivity,
  useRedirectToH5,
  useOpenReplayIdentity,
  useTrackerWelcomeAutoshow,
  useDetailGestureBackSync,
  useStatusBarStyleSync,
} from "@hooks";

import {
  notifyTurnstileError,
  notifyTurnstileSuccess,
  registerTurnstileRenderRequester,
} from "@/src/lib/turnstile/manager";

import { AppStore } from "@/src/store/modules/app";
import { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";
import OpenReplayProvider from "@/src/components/OpenReplayProvider";
import { ROUTER_PATH } from "@/src/config/router";
import { ShowOnPcOrMobile } from "@/src/components/ShowOnPcOrMobile";
import { TrackInitializer } from "./TrackInitializer";
import dynamic from "next/dynamic";
import { i18nStorage } from "../utils";
import { isImmersiveRoute } from "@/src/config/immersiveRoutes";
import log from "logbeacon";
import { shouldApplyMobileRootSafeAreaTop } from "@/src/utils/safeAreaPolicy";
import { useAppFeatures } from "@/src/lib/bridge/useBridge";
import { useFavoritesCenterInit } from "@/src/hooks/useFavoritesCenter";
import { useMemoizedFn } from "ahooks";
import { usePathname } from "@hooks/usePathname";
import { useRouteProtection } from "@/src/hooks/useRouteProtection";
import { initNativeDetailShellHistoryTracking } from "@/src/lib/bridge/shellAppNavigation";
import { useGlobalQuizResultIdCapture } from "@/src/hooks/onboarding/useQuizResultIdCapture";
import {
  registerLoginNavigationHandler,
  unregisterLoginNavigationHandler,
} from "@/src/features/login/loginNavigationBus";
import { useRouter } from "next/navigation";

const Turnstile = dynamic(
  () => import("@marsidev/react-turnstile").then((mod) => mod.Turnstile),
  {
    ssr: false,
  },
);

// const AgentGuidePopupHydrated = lazyHydrate(
//   () =>
//     import("@/src/components/AgentGuidePopup").then(
//       (mod) => mod.AgentGuidePopup,
//     ),
//   {
//     // 3秒后触发 AgentGuidePopupHydrated 初始化
//     idleOptions: {
//       enableIdleCallback: true,
//     },
//     ssr: false,
//     loading: () => null,
//   },
// );

// import { EdgenModalCreateAble } from "@/src/components/EdgenModal";
const EdgenModalCreateAbleRootHydrated = lazyHydrate(
  () =>
    import("@/src/components/EdgenModal").then(
      (mod) => mod.EdgenModalCreateAble.Root,
    ),
  {
    idleOptions: {
      enableIdleCallback: true,
    },
    ssr: false,
    loading: () => null,
  },
);

// EdgenDrawerCallable 统一 Root，ChatModal 等复用此 Root
const EdgenDrawerCallableRootHydrated = lazyHydrate(
  () =>
    import("@/src/components/EdgenDrawerCallable").then(
      (mod) => mod.EdgenDrawerCallable.Root,
    ),
  {
    idleOptions: {
      enableIdleCallback: true,
    },
    ssr: false,
    loading: () => null,
  },
);

const SharePreviewModalCreateAble = lazyHydrate(
  () =>
    import("@/src/components/Chat/SharePreviewModal").then(
      (mod) => mod.SharePreviewModalCreateAble.Root,
    ),
  {
    idleOptions: {
      enableIdleCallback: true,
    },
    ssr: false,
    loading: () => null,
  },
);

// import { EditPortfolioModalCreateAble } from "@/src/components/EditPortfolioModal";
const EditPortfolioModalCreateAbleRootHydrated = lazyHydrate(
  () =>
    import("@/src/components/EditPortfolioModal").then(
      (mod) => mod.EditPortfolioModalCreateAble.Root,
    ),
  {
    idleOptions: {
      enableIdleCallback: true,
    },
    ssr: false,
    loading: () => null,
  },
);

const PortfolioFilePickerHostHydrated = lazyHydrate(
  () =>
    import("@/src/components/PortfolioScreenshotModal/PortfolioFilePickerHost").then(
      (mod) => mod.PortfolioFilePickerHost,
    ),
  {
    ssr: false,
    loading: () => null,
  },
);

/** Must mount eagerly — lazyHydrate without idle/observer never hydrates. */
const HandoffHostClient = dynamic(
  () =>
    import("@/src/components/onboarding/handoff").then(
      (mod) => mod.HandoffHost,
    ),
  { ssr: false },
);

const MenuContentHydrated = lazyHydrate(
  () => import("@components").then((mod) => mod.MenuContent),
  {
    ssr: true,
    displayImmediatelyOnPC: true,
  },
);

// ExtensionSlideMenu
const ExtensionSlideMenuHydrated = lazyHydrate(
  () =>
    import("@/src/extension/SlideMenu").then((mod) => mod.ExtensionSlideMenu),
  {
    observerOptions: {
      wrapperElement: "div",
      rootMargin: "150px 0px 150px 0px",
      enableObserverVisible: true,
    },
    idleOptions: {
      enableIdleCallback: true,
    },
    ssr: false,
    loading: () => (
      <div className="bg-background_white_overlay5 flex flex-col justify-between h-full mx-2 mb-6"></div>
    ),
  },
);

// 需要隐藏aside的路由配置
const LANDING_PAGE_ROUTES = [
  "/aura/[handle]", // 匹配 /aura/xxx 格式的动态路由
  "/task/invite/[inviteCode]", // 匹配 /task/invite/xxx 格式的邀请页面
  "/sign_in",
  "/login",
];

const checkIsLandingPage = (pathname: string): boolean => {
  // 移除语言前缀 (如 /en, /zh 等)
  // 使用更严格的正则表达式，确保只匹配真正的语言代码
  const pathWithoutLocale = pathname.replace(
    /^\/[a-z]{2}(?:-[A-Z]{2})?(?=\/|$)/,
    "",
  );

  return LANDING_PAGE_ROUTES.some((route) => {
    // 将配置中的动态路由转换为正则表达式
    const regexPattern = route
      .replace(/\[handle\]/g, "[^/]+") // [handle] 替换为匹配非斜杠的字符
      .replace(/\[inviteCode\]/g, "[^/]+") // [inviteCode] 替换为匹配非斜杠的字符
      .replace(/\//g, "\\/"); // 转义斜杠

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(pathWithoutLocale);
  });
};

// 检查是否是Chrome扩展， 只有第一次打开可以检测，其他时候取 AppStore.type
const checkIfChromeExtension = () => {
  //clent only
  if (typeof window === "undefined") {
    return {
      isChromeExtension: false,
      windowId: "",
      // 暂时不支持主题切换
      theme: "dark",
      isUtmSource: false,
    };
  }
  const isInIframe = deviceDetector.checkIfInChromeExtensionIframe();
  const currentURL = new URL(window.location.href);
  const utmSource = currentURL.searchParams.get("utm_source");
  const utmWindowId = currentURL.searchParams.get("utm_window_id") || undefined;

  const isUtmSource = utmSource === "chrome-extension";
  const isExtensionMode = localStorage.getItem("extension_mode") === "1";
  const isChromeExtension = (isExtensionMode || isUtmSource) && isInIframe;
  // 遇到页面崩溃，导致恢复时候，url 参数丢失，导致无法恢复插件模式
  if (isChromeExtension) {
    // 只有在插件模式会保存，其他模式不会保存, isChromeExtension = false 时候，可能是参数丢失，导致无法恢复插件模式，不能覆盖extension_mode
    localStorage.setItem("extension_mode", "1");
  }
  return {
    isChromeExtension,
    windowId: utmWindowId,
    // 暂时不支持主题切换
    theme: "dark",

    // 暂时没用到
    isUtmSource,
  };
};

// 不要 overflow-auto — 外层 #container 滚动会令子级 sticky（日历周条等）失效
const isRadarPage = (pathname: string): boolean => {
  return (
    pathname.includes(ROUTER_PATH.RADAR_HK_STOCK) ||
    pathname.includes(ROUTER_PATH.RADAR_US_STOCK) ||
    // pathname.includes(ROUTER_PATH.INVEST_ACTIVITY) ||
    pathname.includes(ROUTER_PATH.MARKET_CRYPTO)
  );
};

/**
 * 移动端布局组件
 * 用于 H5 和 App 一体化的布局，统一处理移动端（包括 H5 和 App）的页面布局
 * 支持响应式设计，适配不同移动设备尺寸
 *
 * 安全区处理：
 * - 与桥接 `mergeFeaturesWithOptimistic` 一致：未声明 `immersiveNavbar` 时乐观默认需要 Web 端 `pt-safe`
 * - 仅当原生显式 `immersiveNavbar === false`（老版本 App，非全屏 WebView）时不加，由系统原生处理
 * - 沉浸式路由（isImmersiveRoute=true）不在根组件加顶部间距，由页面自己处理
 * - 沉浸式路由配置见 @/src/config/immersiveRoutes.ts
 */
interface MobileLayoutProps {
  children: React.ReactNode;
  isLandingPage: boolean;
  noAutoScroll: boolean;
  remainingMs: number;
  isClientReady: boolean;
  /** 是否为沉浸式路由，沉浸式路由不在根组件处理安全区顶部间距 */
  isImmersiveRoute: boolean;
  /** `features.immersiveNavbar`；undefined 表示未声明，按乐观默认加 `pt-safe` */
  supportsImmersiveNavbar: boolean | undefined;
  turnstileVisible: boolean;
}

const MobileLayout: React.FC<MobileLayoutProps> = ({
  children,
  isLandingPage,
  noAutoScroll,
  isClientReady,
  isImmersiveRoute,
  supportsImmersiveNavbar,
  turnstileVisible,
}) => {
  const needsSafeAreaTop = shouldApplyMobileRootSafeAreaTop(
    supportsImmersiveNavbar,
    isImmersiveRoute,
  );

  return (
    <div
      className={cn(
        "min-h-dvh bg-background_solid_neutral_secondary flex-col h-[100dvh] !md:pt-2 !md:pr-2",
        needsSafeAreaTop && "pt-safe",
      )}
    >
      <main
        aria-label="Root Layout Client main"
        data-openreplay-label="Root Layout Client main Semantic"
        id="sem-root-layout-client-main"
        className={cn(
          "md:flex-1 flex-col w-full md:w-full h-full justify-center flex overflow-hidden",
          // "transition-all duration-300 ease-in-out",
          "rounded-t-0 bg-background_solid_neutral_secondary md:border-[0px] md:border-b-0 max-md:border-b-0",
          isLandingPage && "md:w-full md:h-full md:border-none",
        )}
      >
        <div
          id="container"
          className={cn(
            "justify-center flex h-full no-scrollbar",
            !noAutoScroll && "overflow-y-auto",
          )}
        >
          {isClientReady && (
            <>
              <EdgenModalCreateAbleRootHydrated />
              <EdgenDrawerCallableRootHydrated />
              <SharePreviewModalCreateAble />
              <EditPortfolioModalCreateAbleRootHydrated />
              <PortfolioFilePickerHostHydrated />
              <HandoffHostClient />
            </>
          )}
          {children}
        </div>
      </main>
      {/* 非 App 环境才显示 Turnstile */}
      {!DISABLE_TURNSTILE &&
        isClientReady &&
        !deviceDetector.isApp() &&
        turnstileVisible && (
          <Turnstile
            siteKey="0x4AAAAAABAR2O6-zhtwdI9h"
            options={{
              appearance: "interaction-only",
            }}
            onSuccess={(token) => {
              notifyTurnstileSuccess(token);
            }}
            onError={(error) => {
              log.info("[文案][Turnstile] 验证失败", error);
              notifyTurnstileError(error);
            }}
            className="w-0 h-0"
          />
        )}
    </div>
  );
};

/**
 * 默认布局组件（包含侧边栏）
 */
interface DefaultLayoutProps {
  children: React.ReactNode;
  locale?: string;
  isChromeExtension: boolean;
  isLandingPage: boolean;
  isExcludedRoute: boolean;
  noAutoScroll: boolean;
  remainingMs: number;
  isClientReady: boolean;
}

const DefaultLayout: React.FC<DefaultLayoutProps> = ({
  children,
  locale,
  isChromeExtension,
  isLandingPage,
  isExcludedRoute,
  noAutoScroll,
  isClientReady,
}) => {
  return (
    <div
      className={cn(
        "md:flex min-h-screen bg-background_solid_tertiary flex-col h-[100dvh]",
        isChromeExtension && "py-0 h-full",
      )}
      data-crikket-dom-pick-block
    >
      <div
        className={cn(
          "flex h-full",
          isChromeExtension ? "flex-row-reverse" : "",
        )}
      >
        {isExcludedRoute || isLandingPage ? null : isChromeExtension ? (
          <aside
            aria-label="Root Layout Client aside"
            data-openreplay-label="Root Layout Client aside Semantic"
            id="sem-root-layout-client-aside"
            className="w-16 z-100"
            data-tracking-scope="menu-layout"
          >
            <ExtensionSlideMenuHydrated />
          </aside>
        ) : (
          <aside
            aria-label="Root Layout Client aside"
            data-openreplay-label="Root Layout Client aside Semantic"
            id="sem-root-layout-client-aside-2"
            className={cn(
              "max-w-[292px] md:block bg-background_solid_tertiary",
              "hidden",
            )}
            data-tracking-scope="menu-layout-aside"
          >
            <ShowOnPcOrMobile showMode="pc">
              <MenuContentHydrated locale={locale} forceHydrate={true} />
            </ShowOnPcOrMobile>
          </aside>
        )}
        <main
          aria-label="Root Layout Client main"
          data-openreplay-label="Root Layout Client main Semantic"
          id="sem-root-layout-client-main-2"
          className={cn(
            "md:flex-1 flex-col w-full md:w-[calc(100%-300px)] h-full justify-center flex overflow-hidden",
            // "transition-all duration-300 ease-in-out",
            "rounded-t-0 bg-background_solid_secondary md:border-[0px] md:border-b-0 max-md:border-b-0",
            isChromeExtension &&
              " w-[calc(100%-64px)] h-[100vh] bg-background_solid_secondary border-t-[1px] border-r-[1px] border-background_white_overlay5 rounded-tl-none",
            isLandingPage && "md:w-full md:h-full md:border-none",
          )}
        >
          <div
            id="container"
            className={cn(
              "justify-center flex h-full no-scrollbar",
              !noAutoScroll && "overflow-y-auto",
            )}
          >
            {isClientReady && (
              <>
                <EdgenModalCreateAbleRootHydrated />
                <EdgenDrawerCallableRootHydrated />
                <SharePreviewModalCreateAble />
                <EditPortfolioModalCreateAbleRootHydrated />
                <PortfolioFilePickerHostHydrated />
                <HandoffHostClient />
              </>
            )}
            {children}
          </div>
        </main>
      </div>
      {!DISABLE_TURNSTILE && isClientReady && (
        <Turnstile
          siteKey="0x4AAAAAABAR2O6-zhtwdI9h"
          options={{
            appearance: "interaction-only",
          }}
          onSuccess={(token) => {
            notifyTurnstileSuccess(token);
          }}
          onError={(error) => {
            log.info("[文案][Turnstile] 验证失败", error);
            notifyTurnstileError(error);
          }}
          className="w-0 h-0"
        />
      )}
    </div>
  );
};

export function RootLayoutClient({
  children,
  locale,
  deviceType,
  isMobile = false,
}: {
  children: React.ReactNode;
  locale: string;
  deviceType?: DeviceType;
  isMobile?: boolean;
}) {
  // 检测平台（客户端自动检测，服务端传入 undefined）
  // 注意：在客户端，DeviceDetector 会自动检测平台，这里只需要设置基础信息
  deviceDetector.setServerDeviceType(deviceType, isMobile);
  const { remainingMs } = usePricingActivity();
  i18nStorage.setLocale(locale);
  const [isPC] = useIsPc();
  const [isClientReady, setIsClientReady] = useState(false);
  const [turnstileVisible, setTurnstileVisible] = useState(false);

  useEffect(() => {
    setIsClientReady(true);
  }, []);

  useEffect(() => {
    registerTurnstileRenderRequester((visible) => {
      setTurnstileVisible(visible);
    });
    return () => registerTurnstileRenderRequester(null);
  }, []);

  const appRouter = useRouter();
  useEffect(() => {
    registerLoginNavigationHandler((url) => appRouter.replace(url));
    return () => unregisterLoginNavigationHandler();
  }, [appRouter]);

  useRedirectToH5();
  useInitApp({ isClientReady });
  useContainerBroadcastStateSync();
  useNativeBridgeReconcile();
  useCrossWebViewAuthSessionSync();
  useNativeTabFreezeSync();
  useAdjustNativeIntegration();
  useOpenReplayIdentity();
  const pathname = usePathname();
  useFavoritesCenterInit();
  useGlobalQuizResultIdCapture();
  // Protect routes that require authentication
  useRouteProtection();
  useDetailGestureBackSync();
  useStatusBarStyleSync();

  // 统一添加处理收藏夹数据的 Hook
  useFetchUserFolders();

  // alpha 检测
  useAlphaModal();
  useTrackerWelcomeAutoshow();

  // 🔧 ISR 缓存恢复错误保护
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // 捕获 "Cannot read properties of undefined (reading 'call')" 错误
      if (event.message?.includes("Cannot read properties of undefined")) {
        log.warn("[ISR Recovery] Detected stale cache error, reloading page");
        // 标记为缓存恢复错误，避免重复刷新
        const hasReloaded = sessionStorage.getItem("isr_cache_reload");
        if (!hasReloaded) {
          sessionStorage.setItem("isr_cache_reload", "1");
          window.location.reload();
        }
      }
    };

    window.addEventListener("error", handleError);

    // 清除重载标记（页面成功加载后）
    const clearReloadFlag = setTimeout(() => {
      sessionStorage.removeItem("isr_cache_reload");
    }, 5000);

    return () => {
      window.removeEventListener("error", handleError);
      clearTimeout(clearReloadFlag);
    };
  }, []);
  const isExcludedRoute = useMemo(() => {
    return (
      pathname.includes("/extension/login") ||
      pathname.includes("/extension/createProfile") ||
      pathname.includes("/extension/logout") ||
      pathname.includes(ROUTER_PATH.ONBOARDING) ||
      pathname.includes(ROUTER_PATH.WELCOME)
    );
  }, [pathname]);
  const { isChromeExtension, windowId } = useMemo(checkIfChromeExtension, []);

  // iOS WKWebView/H5：点击输入控件之外时 blur，收起键盘（全局生效，避免页面级漏处理）
  useDismissKeyboardOnOutsideTap({
    enabled: deviceType === "ios",
    // 仅在标记了作用域的区域内生效（例如 EmailLogin）
    scopeSelector: "[data-dismiss-kbd-scope]",
    // 延后到下一轮任务，减少 blur 影响点击链路的概率
    blurDelayMs: 0,
  });

  // 判断是否需要隐藏aside
  const isLandingPage = useMemo(() => checkIsLandingPage(pathname), [pathname]);

  // 判断是否为沉浸式路由（不在根组件处理安全区顶部间距）
  const isImmersive = useMemo(() => isImmersiveRoute(pathname), [pathname]);

  // 获取 App 功能特性；不在这里把 undefined 压成 false，便于根布局与乐观默认策略一致
  const appFeatures = useAppFeatures();
  const supportsImmersiveNavbar = appFeatures.immersiveNavbar;

  const themeChange = useMemoizedFn(() => {
    // 暂时不支持主题切换
  });

  const commonHandlers: ExtensionToIframeCommonHandlers = useMemo(
    () => ({
      themeChange,
    }),
    [themeChange],
  );
  useExtensionListener(isChromeExtension, windowId, commonHandlers);

  // 处理 App 初始化进入页面
  useEffect(() => {
    if (isPC || isChromeExtension) {
      return;
    }
    const cleanup = () => {
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.IS_FIRST_LOAD);
    };
    // 监听用户退出网站或关闭PWA的事件
    const handleBeforeUnload = () => {
      cleanup();
    };

    // 监听页面隐藏事件（用户关闭标签页或PWA进程）
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // 页面完全隐藏时清除标记
        cleanup();
      }
    };

    // 监听页面卸载事件（更适用于PWA）
    const handlePageHide = () => {
      cleanup();
    };

    // 添加事件监听器
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      // 清理事件监听器
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [isPC, isChromeExtension]);

  // 处理 auth_store 相关的存储事件
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleStorage = async (event: StorageEvent) => {
      if (event.key === "auth_store") {
        clearTimeout(timeoutId); // 清除上一个定时器，避免多次执行
        timeoutId = setTimeout(() => {
          // 只匹配 chat/{id} 格式的路径，而不是包含 /chat 的所有路径
          const chatIdRegex = /\/chat\/[^\/]+$/;
          if (pathname && chatIdRegex.test(pathname)) {
            localStorage.setItem("create_chat", "1");
          }
          window.location.reload();
        }, 2000);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [pathname]);
  const noAutoScroll = useMemo(() => {
    return isRadarPage(pathname);
  }, [pathname]);

  useEffect(() => {
    try {
      AppStore.type = isChromeExtension ? "extension" : "website";
    } catch (error) {
      log.error("[文案]解析 URL 参数出错:", error);
    }
  }, [isChromeExtension]);

  // 处理弹出窗口的授权码
  useEffect(() => {
    const popupWindowURL = new URL(window.location.href);
    const code = popupWindowURL.searchParams.get("code") as string;
    const error = popupWindowURL.searchParams.get("error") as string;

    if (!!code) {
      setItem("authCode", code);
      window.close();
    }
    if (!!error) {
      setItem("authError", error);
      window.close();
    }
  }, []);

  useEffect(() => {
    initNativeDetailShellHistoryTracking();
  }, [pathname]);

  // 判断是否为移动端：优先使用服务端传入的 isMobile 参数，否则使用客户端自动检测
  // 移动端使用 MobileLayout，桌面端使用 DefaultLayout
  if (isMobile || deviceDetector.isMobile()) {
    return (
      <MobileLayout
        isLandingPage={isLandingPage}
        noAutoScroll={noAutoScroll}
        remainingMs={remainingMs}
        isClientReady={isClientReady}
        isImmersiveRoute={isImmersive}
        supportsImmersiveNavbar={supportsImmersiveNavbar}
        turnstileVisible={turnstileVisible}
      >
        <OpenReplayProvider />
        {children}
        <TrackInitializer />
      </MobileLayout>
    );
  }

  return (
    <DefaultLayout
      locale={locale}
      isChromeExtension={isChromeExtension}
      isLandingPage={isLandingPage}
      isExcludedRoute={isExcludedRoute}
      noAutoScroll={noAutoScroll}
      remainingMs={remainingMs}
      isClientReady={isClientReady}
    >
      <OpenReplayProvider />
      {children}
    </DefaultLayout>
  );
}
